import { useCallback, useMemo, useState } from 'react';
import type { BranchColorRule, CommitDTO } from '@clearflow/shared';

export interface BranchEntry {
  name: string;
  headSha: string;
  isDefault: boolean;
  isProtected: boolean;
  /** Branch deleted on the server but still inferable from a merge-commit subject */
  isDeleted?: boolean;
}

// Groupes fixes, historiques — classement par préfixe, utilisé en repli pour toute branche
// qu'aucune règle de couleur ne couvre. Chaque règle de couleur (BranchColorRule.label,
// obligatoire — voir branchColors.ts) ajoute en plus un groupe dynamique, clé `rule:${index}`
// (l'index dans colorRules, stable tant que l'équipe ne réordonne pas ses règles) — y compris
// pour main/master : une règle qui les couvre (ex: "Main" sur `^main$`) remplace "Principale" par
// son propre nom (voir getGroup). Le traitement spécial de main/master (case à cocher non
// désactivable, exclu de setGroupSelection, case "tout sélectionner" masquée) ne dépend jamais de
// la clé de groupe mais d'isMainBranch(name) directement — il reste donc valable quel que soit le
// groupe, fixe ou dynamique, où la branche atterrit.
const FIXED_GROUP_ORDER = ['main', 'release', 'feature', 'fix', 'chore', 'other'] as const;
type FixedGroup = (typeof FIXED_GROUP_ORDER)[number];
type RuleGroup = `rule:${number}`;
type Group = FixedGroup | RuleGroup;

const GROUP_LABELS: Record<FixedGroup, string> = {
  main:    'Principale',
  release: 'Release',
  feature: 'Feature',
  fix:     'Fix',
  chore:   'Chore',
  other:   'Autre',
};

const ACTIVE_DAYS = 30;
const ACTIVE_MS = ACTIVE_DAYS * 24 * 3600 * 1000;
// Exporté : sert de défaut côté RepoViewPage.tsx pour initialiser l'état persisté
// (collapsedBranchGroups, préférences) avant que preferencesQuery n'ait résolu.
export const DEFAULT_COLLAPSED_BRANCH_GROUPS: string[] = ['feature', 'fix', 'chore', 'other'];

interface Props {
  branches: BranchEntry[];
  commits: CommitDTO[];
  /** parent branch name → direct children branch names */
  descendantsOf: Map<string, string[]>;
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  // Clic sur l'item (hors case à cocher elle-même, voir branch-row plus bas) — recentre le
  // graphe sur le label de cette branche, sans toucher à sa sélection.
  onFlyToBranch: (name: string) => void;
  /** Règles de couleur du dépôt — chacune crée aussi un groupe (voir FIXED_GROUP_ORDER). */
  colorRules: BranchColorRule[];
  // Plié/déplié — contrôlé depuis RepoViewPage.tsx (comme selected/onChange ci-dessus) pour
  // persister côté serveur (User.preferences.collapsedBranchGroups) plutôt qu'en state local
  // perdu à chaque remount.
  collapsedGroups: Set<string>;
  onToggleCollapse: (group: string) => void;
}

interface LabeledRule {
  key: RuleGroup;
  label: string;
  re: RegExp;
}

// Précompilées une fois — une regex invalide est ignorée plutôt que de faire planter le
// regroupement : ne devrait jamais arriver (pattern et label validés à l'enregistrement,
// branchColors.ts), défensif comme matchCount dans BranchColorRulesModal.tsx.
function compileLabeledRules(colorRules: BranchColorRule[]): LabeledRule[] {
  const out: LabeledRule[] = [];
  colorRules.forEach((rule, i) => {
    try {
      out.push({ key: `rule:${i}`, label: rule.label, re: new RegExp(rule.pattern) });
    } catch {
      // pattern invalide — ignoré, voir commentaire ci-dessus
    }
  });
  return out;
}

function gatherDescendants(branch: string, tree: Map<string, string[]>): Set<string> {
  const out = new Set<string>();
  const queue = [branch];
  while (queue.length) {
    const cur = queue.shift()!;
    for (const child of tree.get(cur) ?? []) {
      if (!out.has(child)) {
        out.add(child);
        queue.push(child);
      }
    }
  }
  return out;
}

// main/master or any main-family branch (main-next, main/foo…) — boundary-aware
// so it doesn't also match e.g. "maintenance/1.2".
function isMainBranch(name: string): boolean {
  return name === 'master' || /^main(-|\/|$)/.test(name);
}

function getGroup(branch: BranchEntry, labeledRules: LabeledRule[]): Group {
  // Première règle qui matche l'emporte, même ordre de priorité que pour la couleur
  // (branchColors.ts) — y compris pour main/master : si l'équipe a défini une règle qui les
  // couvre (ex: "Main" sur `^main$`), son nom remplace "Principale". Le traitement spécial de
  // main/master (case à cocher non désactivable, exclusion de setGroupSelection) ne dépend pas
  // de la clé de groupe mais d'isMainBranch(branch.name) directement (voir plus bas dans ce
  // fichier) — il reste donc intact quel que soit le groupe où la branche atterrit ici.
  for (const lr of labeledRules) {
    if (lr.re.test(branch.name)) return lr.key;
  }
  // Named main/master (or any main-family branch, e.g. main-next) → Principale par défaut, tant
  // qu'aucune règle ne l'a déjà capturée ci-dessus.
  if (isMainBranch(branch.name)) return 'main';
  // La branche par défaut du dépôt (GitLab defaultBranch) va toujours en "Principale", avant même
  // le classement par préfixe — sur GitLab, le defaultBranch peut être une branche release/* (ou
  // feature/*, chore/*…) sans rapport avec main/master (voir buildGitgraphModel.ts, même gotcha),
  // et reste la branche de référence du dépôt quel que soit son nom.
  if (branch.isDefault) return 'main';
  const slash = branch.name.indexOf('/');
  if (slash !== -1) {
    const prefix = branch.name.slice(0, slash);
    if (prefix === 'feature' || prefix === 'feat') return 'feature';
    if (prefix === 'fix')     return 'fix';
    if (prefix === 'chore')   return 'chore';
    if (prefix === 'release') return 'release';
  }
  return 'other';
}

function setsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
}

function relativeTime(iso?: string): string {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60)         return "à l'instant";
  if (diff < 3600)       return `il y a ${Math.round(diff / 60)} min`;
  if (diff < 86400)      return `il y a ${Math.round(diff / 3600)} h`;
  if (diff < 86400 * 7)  return `il y a ${Math.round(diff / 86400)} j`;
  if (diff < 86400 * 30) return `il y a ${Math.round(diff / 86400 / 7)} sem`;
  if (diff < 86400 * 365)return `il y a ${Math.round(diff / 86400 / 30)} mois`;
  return `il y a ${Math.round(diff / 86400 / 365)} ans`;
}

export function BranchFilter({
  branches, commits, descendantsOf, selected, onChange, onFlyToBranch, colorRules, collapsedGroups, onToggleCollapse,
}: Props) {
  const [search, setSearch] = useState('');

  const commitBySha = useMemo(() => new Map(commits.map((c) => [c.sha, c])), [commits]);
  const dateOf = useCallback(
    (b: BranchEntry) => commitBySha.get(b.headSha)?.authoredDate,
    [commitBySha],
  );

  const labeledRules = useMemo(() => compileLabeledRules(colorRules), [colorRules]);

  // Groupes de règles insérés entre Principale et les groupes fixes historiques — une convention
  // d'équipe explicite prend le pas visuellement sur le classement par défaut, sans le remplacer
  // pour les branches qu'aucune règle ne couvre.
  const groupOrder = useMemo<Group[]>(() => {
    const [main, ...rest] = FIXED_GROUP_ORDER;
    return [main, ...labeledRules.map((lr) => lr.key), ...rest];
  }, [labeledRules]);
  const groupLabel = useCallback(
    (g: Group): string =>
      g in GROUP_LABELS ? GROUP_LABELS[g as FixedGroup] : labeledRules.find((lr) => lr.key === g)!.label,
    [labeledRules],
  );

  const isSearching = search.trim().length > 0;

  const filtered = useMemo(() => {
    if (!isSearching) return branches;
    const q = search.trim().toLowerCase();
    return branches.filter((b) => b.name.toLowerCase().includes(q));
  }, [branches, search, isSearching]);

  const grouped = useMemo(() => {
    const g = new Map<Group, BranchEntry[]>();
    for (const b of filtered) {
      const key = getGroup(b, labeledRules);
      if (!g.has(key)) g.set(key, []);
      g.get(key)!.push(b);
    }
    for (const [, list] of g) {
      list.sort((a, b) => ((dateOf(a) ?? '') < (dateOf(b) ?? '') ? 1 : -1));
    }
    return g;
  }, [filtered, dateOf, labeledRules]);

  const isOpen = (g: Group) => isSearching || !collapsedGroups.has(g);

  const toggleBranch = (name: string) => {
    if (isMainBranch(name)) return;
    const next = new Set(selected);
    const descendants = gatherDescendants(name, descendantsOf);
    if (next.has(name)) {
      next.delete(name);
      for (const d of descendants) next.delete(d);
    } else {
      next.add(name);
      for (const d of descendants) next.add(d);
    }
    onChange(next);
  };

  const setGroupSelection = (list: BranchEntry[], value: boolean) => {
    const next = new Set(selected);
    for (const b of list) {
      if (!value && isMainBranch(b.name)) continue;
      const descendants = gatherDescendants(b.name, descendantsOf);
      if (value) {
        next.add(b.name);
        for (const d of descendants) next.add(d);
      } else {
        next.delete(b.name);
        for (const d of descendants) next.delete(d);
      }
    }
    onChange(next);
  };

  // Sets des trois préréglages (Tout/Aucune/Actives) — mémoïsés à la fois pour les boutons
  // eux-mêmes et pour détecter si `selected` correspond exactement à l'un d'eux (activePreset
  // ci-dessous), afin de le mettre en surbrillance.
  const allNames = useMemo(() => new Set(branches.map((b) => b.name)), [branches]);
  const noneNames = useMemo(
    () => new Set(branches.filter((b) => isMainBranch(b.name)).map((b) => b.name)),
    [branches],
  );
  const activeNames = useMemo(() => {
    const next = new Set<string>();
    for (const b of branches) {
      if (isMainBranch(b.name)) { next.add(b.name); continue; }
      const iso = dateOf(b);
      if (iso && Date.now() - new Date(iso).getTime() < ACTIVE_MS) next.add(b.name);
    }
    return next;
  }, [branches, dateOf]);

  const activePreset = useMemo(() => {
    if (setsEqual(selected, allNames)) return 'all';
    if (setsEqual(selected, noneNames)) return 'none';
    if (setsEqual(selected, activeNames)) return 'actives';
    return null;
  }, [selected, allNames, noneNames, activeNames]);

  return (
    <div className="branch-filter-card">
      <div className="branch-filter-toolbar">
        <div className="commit-search-field">
          <svg className="commit-search-icon" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" strokeWidth="1.5"/>
            <line x1="10" y1="10" x2="14.5" y2="14.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <input
            className="commit-search-input"
            type="text"
            placeholder="Filtrer les branches…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            spellCheck={false}
            style={search ? { paddingRight: 28 } : undefined}
          />
          {search && (
            <button
              type="button"
              className="commit-search-clear"
              aria-label="Effacer le filtre"
              onClick={() => setSearch('')}
            >
              <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="12" height="12">
                <line x1="3" y1="3" x2="13" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <line x1="13" y1="3" x2="3" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </div>
        <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
          <button
            className={`secondary branch-preset-btn${activePreset === 'all' ? ' active' : ''}`}
            onClick={() => onChange(new Set(allNames))}
          >
            Tout
          </button>
          <button
            className={`secondary branch-preset-btn${activePreset === 'none' ? ' active' : ''}`}
            onClick={() => onChange(new Set(noneNames))}
          >
            Aucune
          </button>
          <button
            className={`secondary branch-preset-btn${activePreset === 'actives' ? ' active' : ''}`}
            onClick={() => onChange(new Set(activeNames))}
            title={`Branche par défaut + branches modifiées dans les ${ACTIVE_DAYS} derniers jours`}
          >
            Actives {ACTIVE_DAYS}j
          </button>
          <span className="muted" style={{ marginLeft: 'auto' }}>
            {selected.size} / {branches.length}
            {isSearching && ` · ${filtered.length} affichées`}
          </span>
        </div>
      </div>

      {groupOrder.map((g) => {
        const list = grouped.get(g);
        if (!list || list.length === 0) return null;
        const open = isOpen(g);
        const selCount = list.filter((b) => selected.has(b.name)).length;
        const allSelected = selCount === list.length;
        const indeterminate = selCount > 0 && selCount < list.length;
        // Case "tout sélectionner" masquée quand le groupe ne contient que des branches
        // main-family : leur case individuelle est déjà non désactivable (isMainBranch), une case
        // groupée serait cliquable sans jamais rien changer. Généralisé au-delà de la seule clé
        // fixe 'main' : une règle nommée qui capture main/master (voir getGroup) déplace ces
        // branches vers un groupe personnalisé sans changer cet invariant.
        const allMainFamily = list.every((b) => isMainBranch(b.name));

        return (
          <div key={g} className="branch-group">
            <div className="branch-group-header">
              <button className="branch-group-toggle" onClick={() => onToggleCollapse(g)} type="button">
                <span className="branch-group-caret">{open ? '▼' : '▶'}</span>
                <span>{groupLabel(g)}</span>
                <span className="muted">({list.length})</span>
              </button>
              {!allMainFamily && (
                <label className="branch-group-select" title={allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = indeterminate; }}
                    onChange={(e) => setGroupSelection(list, e.target.checked)}
                  />
                  <span>{selCount} / {list.length}</span>
                </label>
              )}
            </div>
            {open && (
              <ul className="branch-list">
                {list.map((b) => (
                  <li key={b.name}>
                    {/* Un <label> englobant faisait basculer la case sur tout clic dans la ligne
                        (comportement natif label→input) — <div> + stopPropagation sur la case
                        elle-même : seul un clic pile sur la case coche/décoche, le reste de la
                        ligne recentre le graphe sur le label de la branche (onFlyToBranch). */}
                    <div
                      className="branch-row"
                      role="button"
                      tabIndex={0}
                      onClick={() => onFlyToBranch(b.name)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onFlyToBranch(b.name);
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(b.name)}
                        disabled={isMainBranch(b.name)}
                        onChange={() => toggleBranch(b.name)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Afficher la branche ${b.name} dans le graphe`}
                      />
                      <span className="branch-name">{b.name}</span>
                      {(b.isDefault || b.isProtected) && (
                        <span className="branch-badges">
                          {b.isDefault && (
                            <span className="badge-icon badge-default" title="Branche par défaut">D</span>
                          )}
                          {b.isProtected && (
                            <span className="badge-icon badge-protected" title="Branche protégée">P</span>
                          )}
                        </span>
                      )}
                      <span className="muted branch-age">{relativeTime(dateOf(b))}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}

      {isSearching && filtered.length === 0 && (
        <p className="muted" style={{ padding: 8, margin: 0 }}>
          Aucune branche ne correspond à « {search} ».
        </p>
      )}
    </div>
  );
}
